import React from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { BreakdownRow } from "../types";
import { fmt } from "../utils/format";

export interface BreakdownSection {
  title?: string; // optional sub-heading, e.g. "Partner Deposits"
  rows: BreakdownRow[];
}

interface Props {
  title: string;
  columnLabel: string; // "Income Source" / "Expense Category"
  sections: BreakdownSection[];
  totalLabel: string;
  total: number;
  headerBg: string;
  totalColor: string;
  emptyLabel: string;
  onRowClick?: (row: BreakdownRow) => void;
}

/**
 * Two-column "name | amount" table with a total row, used for both Detailed
 * Income and Detailed Expenses. Clicking a row opens its drill-down.
 */
const BreakdownTable: React.FC<Props> = ({
  title,
  columnLabel,
  sections,
  totalLabel,
  total,
  headerBg,
  totalColor,
  emptyLabel,
  onRowClick,
}) => {
  const hasRows = sections.some((s) => s.rows.length > 0);
  const amountText = fmt;

  return (
    <Card elevation={2} sx={{ borderRadius: 2, height: "100%" }} className="bs-section">
      <Typography variant="subtitle1" fontWeight={700} sx={{ px: 2, pt: 1.5, pb: 1 }}>
        {title}
      </Typography>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: headerBg }}>
              <TableCell sx={{ fontWeight: 700 }}>{columnLabel}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, width: 150 }}>
                Amount
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!hasRows && (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ color: "text.secondary", py: 3 }}>
                  {emptyLabel}
                </TableCell>
              </TableRow>
            )}
            {sections.map((section) =>
              section.rows.length === 0 ? null : (
                <React.Fragment key={section.title || "main"}>
                  {section.title && (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{ fontSize: "0.75rem", fontWeight: 700, color: "text.secondary", pt: 1.5, textTransform: "uppercase" }}
                      >
                        {section.title}
                      </TableCell>
                    </TableRow>
                  )}
                  {section.rows.map((row) => (
                    <TableRow
                      key={`${row.kind}-${row.id}`}
                      hover={!!onRowClick}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      sx={{ cursor: onRowClick ? "pointer" : "default" }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {row.name}
                          <Typography variant="caption" color="text.secondary">
                            ({row.count})
                          </Typography>
                          {onRowClick && (
                            <ChevronRightIcon
                             
                              fontSize="small"
                              sx={{ color: "text.disabled", ml: "auto" }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {amountText(row.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              )
            )}
            <TableRow sx={{ backgroundColor: headerBg }}>
              <TableCell sx={{ fontWeight: 700 }}>{totalLabel}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: totalColor, whiteSpace: "nowrap" }}>
                {amountText(total)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default BreakdownTable;
